/**
 * lazyWithRetry — drop-in replacement for React.lazy that recovers from
 * stale-deploy chunk-missing errors.
 *
 * The problem: when Vercel deploys a new build, every JS chunk gets a new
 * filename hash (e.g. `DashboardView-WIr8nnWX.js` → `DashboardView-A2bXc91.js`).
 * Users with the OLD index.html cached in a tab still reference the OLD
 * chunk hash. Navigating to a route that needs that chunk fires the dynamic
 * import, the browser fetches the now-deleted file, and React throws:
 *
 *   "Failed to fetch dynamically imported module:
 *      https://razaproapp.com/assets/DashboardView-WIr8nnWX.js"
 *
 * The screen is blank and the user sees nothing.
 *
 * The fix: catch that specific error class, try the import once more
 * (covers transient network blips), then if still failing force a full
 * `window.location.reload()`. The reload re-fetches index.html from the
 * server, picks up the latest chunk hashes, and the user lands on the
 * page they were trying to reach with everything working.
 *
 * Combined with `useNewBuildDetector` (which prompts on visible new
 * deploys), the entire "stale tab + new deploy" failure mode becomes
 * self-healing without the user having to know what happened.
 */
import React from 'react';

interface LazyImporter<T extends React.ComponentType<any>> {
  (): Promise<{ default: T }>;
}

/** Detect the chunk-missing error class. Different bundlers / browsers
 *  word it slightly differently — match the common variants.
 *
 *  Mobile Safari has its own surface for the same condition: when the
 *  server returns the SPA fallback (text/html) for a missing .js URL,
 *  the browser refuses with "'text/html' is not a valid JavaScript
 *  MIME type." Treat it as a chunk-missing error so the retry+reload
 *  path runs and the user lands on the current build. */
export function isChunkMissingError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? '');
  return /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk \d+ failed|is not a valid JavaScript MIME type|error loading dynamically imported module/i.test(msg);
}

/** sessionStorage flag — once we've reloaded for a chunk error, don't
 *  reload AGAIN if the next attempt also fails. Prevents an infinite reload
 *  loop in the unlikely case the new build also can't serve the chunk. */
const RELOAD_FLAG_KEY = 'razapro:lazyWithRetry:reloadedAt';
const RELOAD_FLAG_TTL_MS = 60_000; // 1 minute

function shouldAvoidReload(): boolean {
  try {
    const ts = sessionStorage.getItem(RELOAD_FLAG_KEY);
    if (!ts) return false;
    const age = Date.now() - parseInt(ts, 10);
    return age >= 0 && age < RELOAD_FLAG_TTL_MS;
  } catch {
    return false;
  }
}

function markReloaded(): void {
  try { sessionStorage.setItem(RELOAD_FLAG_KEY, String(Date.now())); } catch { /* ignore */ }
}

export function lazyWithRetry<T extends React.ComponentType<any>>(importer: LazyImporter<T>): React.LazyExoticComponent<T> {
  return React.lazy(async () => {
    try {
      return await importer();
    } catch (err) {
      if (!isChunkMissingError(err)) throw err;

      // First-attempt failure on a chunk hash. Most often: stale tab + new
      // deploy. Try once more in case it was a transient network blip.
      // Operational telemetry — the system is recovering as designed.
      // Use info so the patched console doesn't surface this as an admin warning.
      console.info('[lazyWithRetry] chunk import failed, retrying:', err);
      try {
        return await importer();
      } catch (retryErr) {
        if (!isChunkMissingError(retryErr)) throw retryErr;

        // Still failing — almost certainly a stale-tab + new-deploy mismatch.
        // Force a full reload so the browser re-fetches index.html and gets
        // the new chunk hashes. Guard against reload loops via a session flag.
        if (shouldAvoidReload()) {
          // This IS surfaced — reload loops are real failure that needs attention.
          console.error('[lazyWithRetry] chunk import failed after retry AND a recent reload — giving up to avoid loop:', retryErr);
          throw retryErr;
        }
        // Operational telemetry — the forced reload IS the recovery.
        console.info('[lazyWithRetry] chunk import failed after retry — forcing reload to pick up latest deploy');
        markReloaded();
        window.location.reload();
        // Return a never-resolving promise so React.lazy waits while the
        // page reloads. (Without this, the promise rejection still bubbles
        // up before reload completes and shows an error UI for ~1s.)
        return new Promise<{ default: T }>(() => {});
      }
    }
  });
}
